// Staff roles and management-panel sections. Pure constants: imported by the
// server (auth, api, seed) AND bundled into the admin client, so nothing here
// may touch env, the database or any server-only module.

export const sectionKeys = [
  "dashboard",
  "leads",
  "pipeline",
  "customers",
  "orders",
  "shipping",
  "contracts",
  "team",
  "marketing",
  "cashier",
  "installments",
  "reception",
  "appointments",
  "workshop",
  "requests",
  "inventory",
  "warranty",
  "service_reports",
  "tickets",
  "livechat",
  "content",
  "branches",
  "gateways",
  "access",
] as const;
export type SectionKey = (typeof sectionKeys)[number];

export const roleKeys = [
  "superadmin",
  "ceo",
  "ceo_advisor",
  "sales_manager",
  "sales_agent",
  "crm_manager",
  "marketing_manager",
  "service_manager",
  "receptionist",
  "technician",
  "finance",
  "contracts_affairs",
  "office_manager",
  "hr_manager",
  "car_procurement",
  "support_agent",
  "shipping_manager",
  "dealership_supervisor",
] as const;
export type RoleKey = (typeof roleKeys)[number];

export const roleLabels = {
  superadmin: "مدیر سیستم",
  ceo: "مدیرعامل",
  ceo_advisor: "مشاور مدیرعامل",
  sales_manager: "مدیر فروش",
  sales_agent: "کارشناس فروش",
  crm_manager: "مدیر CRM",
  marketing_manager: "مدیر بازاریابی",
  service_manager: "مدیر خدمات",
  receptionist: "پذیرشگر",
  technician: "تکنسین",
  finance: "مالی",
  contracts_affairs: "امور قرداد ها",
  office_manager: "مدیر دفتر",
  hr_manager: "مدیر منابع انسانی",
  car_procurement: "کارشناس تامین خودرو",
  support_agent: "کارشناس پشتیبانی و تماس",
  shipping_manager: "مدیر حمل دریایی",
  dealership_supervisor: "سرپرست نمایندگی",
} satisfies Record<RoleKey, string>;

/** `user.role` is free text in the database; only these keys are staff roles */
export const isRoleKey = (value: string | null | undefined): value is RoleKey =>
  roleKeys.some((key) => key === value);

/** The one role whose access is always complete and that manages users and the matrix. */
export const SUPERADMIN: RoleKey = "superadmin";

/**
 * Buyers and owners who sign in to the customer panel (apps/web) with their phone.
 * Not a staff role: the management panel refuses it everywhere.
 */
export const CUSTOMER_ROLE = "customer";

/** Access matrix a fresh install is seeded with; afterwards it is edited from /access. */
export const defaultRolePermissions = {
  superadmin: [...sectionKeys],
  ceo: sectionKeys.filter((section) => section !== "access"),
  ceo_advisor: [
    "dashboard",
    "leads",
    "pipeline",
    "orders",
    "contracts",
    "team",
    "marketing",
    "service_reports",
  ],
  sales_manager: [
    "dashboard",
    "leads",
    "pipeline",
    "customers",
    "orders",
    "contracts",
    "team",
    "marketing",
    "cashier",
    "installments",
    "tickets",
  ],
  sales_agent: ["leads", "pipeline", "customers", "orders", "team"],
  // answers the phone: customer files (no deleting, no wallet) and tickets; tasks and
  // outbound call rows come through «کارتابل من», which every staff member has
  support_agent: ["customers", "tickets", "livechat"],
  crm_manager: [
    "dashboard",
    "leads",
    "pipeline",
    "customers",
    "orders",
    "contracts",
    "team",
    "marketing",
    "cashier",
    "tickets",
  ],
  marketing_manager: ["dashboard", "customers", "marketing"],
  service_manager: [
    "dashboard",
    "cashier",
    "reception",
    "appointments",
    "workshop",
    "requests",
    "inventory",
    "warranty",
    "service_reports",
    "tickets",
    "customers",
    "branches",
  ],
  receptionist: ["cashier", "reception", "appointments", "workshop", "requests"],
  technician: ["workshop"],
  finance: [
    "dashboard",
    "orders",
    "contracts",
    "cashier",
    "installments",
    "service_reports",
    "gateways",
  ],
  contracts_affairs: ["customers", "orders", "contracts"],
  office_manager: ["dashboard", "team", "tickets", "livechat", "content", "branches"],
  hr_manager: ["dashboard", "team", "branches"],
  car_procurement: ["orders", "requests", "inventory"],
  shipping_manager: ["customers", "shipping"],
  // runs a dealership floor: its leads, deals, customers, till and tickets, no workshop or content
  dealership_supervisor: [
    "dashboard",
    "leads",
    "pipeline",
    "customers",
    "orders",
    "contracts",
    "team",
    "cashier",
    "tickets",
    "branches",
  ],
} satisfies Record<RoleKey, SectionKey[]>;

/** Iranian mobile number in the one canonical form we store: 09xxxxxxxxx */
export const PHONE_PATTERN = /^09\d{9}$/;

/**
 * Digits only, Latin, with a leading 0 for Iranian numbers typed as +98 / 0098 / 9xx.
 * Mirrored client-side by normalizeMobile in @car/ui/lib/form-rules (apps/server/test/phone.test.ts keeps them equal).
 */
export const normalizePhone = (value: string) => {
  const digits = value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/\D/g, "");
  if (digits.startsWith("0098")) return `0${digits.slice(4)}`;
  if (digits.startsWith("98") && digits.length === 12) return `0${digits.slice(2)}`;
  if (digits.startsWith("9") && digits.length === 10) return `0${digits}`;
  return digits;
};
